<template>
  <!--
    Panel kryteriów wyszukiwania historii aktywności.
    Cztery równorzędne pola w jednym rzędzie + akcje. Bez zagnieżdżonych tabel i ozdobników.
  -->
  <form class="activity-filters" @submit.prevent="$emit('search')">
    <div class="activity-filters__grid">
      <!-- Użytkownik -->
      <div class="activity-filters__field">
        <span class="ds-field-label">{{ t('username') }}</span>
        <select class="form-select" name="userId" v-model="store.form.userId">
          <option value="">{{ t('all-users') }}</option>
          <option v-for="user in store.users" :key="user.id" :value="user.id">
            {{ user.name }}
          </option>
        </select>
      </div>

      <!-- Typ zdarzenia -->
      <div class="activity-filters__field">
        <span class="ds-field-label">{{ t('type-event') }}</span>
        <select class="form-select" name="eventType" v-model="store.form.eventType">
          <option value="">{{ t('all-event-types') }}</option>
          <option v-for="id in store.eventTypeIds" :key="id" :value="id">
            {{ eventTypeLabel(id) }}
          </option>
        </select>
      </div>

      <!-- Data od -->
      <div class="activity-filters__field">
        <span class="ds-field-label" :class="{ 'text-danger': hasFieldError('dateTimeStart') }">
          {{ t('date-from') }}
        </span>
        <VueDatePicker
          v-model="store.form.dateTimeStart"
          time-picker-inline
          :format="formatDate"
          :month-change-on-scroll="false"
          locale="pl"
          :cancelText="t('cancel')"
          :selectText="t('select')"
          inputClassName="daterange-input"
          teleport="body"
          :teleport-center="compactDatePicker"
          :menu-class-name="datePickerMenuClass"
        />
      </div>

      <!-- Data do -->
      <div class="activity-filters__field">
        <span class="ds-field-label" :class="{ 'text-danger': hasFieldError('dateTimeEnd') }">
          {{ t('date-to') }}
        </span>
        <VueDatePicker
          v-model="store.form.dateTimeEnd"
          time-picker-inline
          :format="formatDate"
          :month-change-on-scroll="false"
          locale="pl"
          :cancelText="t('cancel')"
          :selectText="t('select')"
          inputClassName="daterange-input"
          teleport="body"
          :teleport-center="compactDatePicker"
          :menu-class-name="datePickerMenuClass"
        />
      </div>
    </div>

    <div class="activity-filters__actions">
      <button type="button" class="btn btn-outline-secondary" @click="store.resetForm()">
        {{ t('clear-filters') }}
      </button>
      <button type="submit" class="btn btn-next" :disabled="loading">
        {{ loading ? t('loading') : t('search').toUpperCase() }}
      </button>
    </div>
  </form>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import { useCompactDatePicker } from "@/utils/datePickerLayout";
// CSS biblioteki ładowany globalnie w `main.js` (kolejność kaskady) — nie importuj go tutaj.
import { useUserActivityFiltersStore } from "@/stores/userActivityFilters";
import { userActivityEventLabel } from "@/constants/userActivityEventTypes";

export default {
  name: "UserActivityFilters",
  components: { VueDatePicker },
  emits: ["search"],
  props: {
    // Czy trwa pobieranie wyników — blokuje ponowne kliknięcie „Szukaj”.
    loading: { type: Boolean, default: false },
  },
  setup() {
    // Na niskich/wąskich ekranach kalendarz otwiera się wyśrodkowany, bo popup
    // „przy polu" nie mieścił się w viewporcie (patrz `utils/datePickerLayout`).
    const { compactDatePicker, datePickerMenuClass } = useCompactDatePicker();

    return {
      store: useUserActivityFiltersStore(),
      compactDatePicker,
      datePickerMenuClass,
    };
  },
  created() {
    this.store.loadFilterValues();
  },
  methods: {
    // Etykieta w polu datepickera: 07.02.2026 14:35
    formatDate(date) {
      const pad = (value) => String(value).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    // Nazwa typu zdarzenia w aktualnym języku (backend zwraca tylko identyfikatory).
    eventTypeLabel(eventType) {
      return userActivityEventLabel(eventType);
    },
    hasFieldError(field) {
      return this.store.error && this.store.requireFields.includes(field);
    },
  },
};
</script>

<style scoped>
.activity-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--spacing-lg);
  padding: var(--spacing-md) var(--spacing-lg);
}

/* Cztery pola o równej szerokości, zawijane na wąskich ekranach. */
.activity-filters__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-md) var(--spacing-lg);
  flex: 1 1 640px;
  min-width: 0;
}

.activity-filters__field {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
}

.activity-filters__field .ds-field-label {
  margin-bottom: var(--spacing-xs);
}

.activity-filters__field .form-select {
  font-size: var(--font-size-sm);
  height: var(--control-height);
}

.activity-filters__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 0 0 auto;
}

.activity-filters__actions .btn {
  min-height: var(--control-height);
  min-width: 120px;
}

/* Pole datepickera renderuje biblioteka — dosuwamy tekst tak, aby nie wchodził
   pod ikonę kalendarza (siedzi absolutnie po lewej i zajmuje ok. 35 px). */
.activity-filters :deep(.dp__input) {
  min-height: var(--control-height);
  padding-left: 35px;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.activity-filters :deep(.dp__input:hover) {
  border-color: var(--border-strong);
}

.activity-filters :deep(.dp__input_focus) {
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px var(--accent-focus-ring);
}
</style>
